import { PostgrestError, PostgrestSingleResponse, SupabaseClient } from '@supabase/supabase-js';

import {
  UpsertMediaManagerChampionshipRenderedImage,
  UpsertMediaManagerRenderedImage,
  UpsertMediaManagerStageRenderedImage,
} from '../../domain/interfaces/supabase/media-manager/actions';
import { MediaManagerRenderedImageDto } from '../../domain/interfaces/supabase/media-manager/dto';
import { MediaManagerRenderedImage } from '../../domain/interfaces/supabase/media-manager/entity';
import mediaManagerRenderedImageDataMapper from '../../domain/mappers/media-manager-rendered-image-data-mapper';
import {
  RenderImageError,
  RenderImageErrorCode,
} from 'modules/manager/domain/render/errors/render-image-errors';

const RENDERED_IMAGES_TABLE = 'media_manager_rendered_images';

/**
 * Backs match renders (row keyed by match_id+template_id), stage/standings
 * renders (stage_id+template_id+group_key) and championship/player-statistics
 * renders (championship_id+template_id+group_key) — one unified table, see
 * 20260710_media_manager_unify_rendered_images.sql and
 * 20260716_media_manager_championship_rendered_images.sql.
 */
export default class MediaManagerRenderedImages {
  constructor(private readonly supabase: SupabaseClient) {}

  async upsert(input: UpsertMediaManagerRenderedImage): Promise<MediaManagerRenderedImage> {
    const { data, error } = (await this.supabase
      .from(RENDERED_IMAGES_TABLE)
      .upsert(
        {
          match_id: input.matchId,
          template_id: input.templateId,
          image_url: input.imageUrl,
        },
        { onConflict: 'match_id,template_id' },
      )
      .select()
      .single()) as PostgrestSingleResponse<MediaManagerRenderedImageDto>;

    if (error) {
      throw classifyPostgrestError(error, 'upsert', {
        matchId: input.matchId,
        templateId: input.templateId,
      });
    }

    return mediaManagerRenderedImageDataMapper(data);
  }

  async upsertStage(
    input: UpsertMediaManagerStageRenderedImage,
  ): Promise<MediaManagerRenderedImage> {
    const { data, error } = (await this.supabase
      .from(RENDERED_IMAGES_TABLE)
      .upsert(
        {
          stage_id: input.stageId,
          template_id: input.templateId,
          group_key: input.groupKey,
          group_id: input.groupId ?? null,
          group_name: input.groupName ?? null,
          image_url: input.imageUrl,
        },
        { onConflict: 'stage_id,template_id,group_key' },
      )
      .select()
      .single()) as PostgrestSingleResponse<MediaManagerRenderedImageDto>;

    if (error) {
      throw classifyPostgrestError(error, 'upsert', {
        stageId: input.stageId,
        templateId: input.templateId,
        groupKey: input.groupKey,
      });
    }

    return mediaManagerRenderedImageDataMapper(data);
  }

  async upsertChampionship(
    input: UpsertMediaManagerChampionshipRenderedImage,
  ): Promise<MediaManagerRenderedImage> {
    const { data, error } = (await this.supabase
      .from(RENDERED_IMAGES_TABLE)
      .upsert(
        {
          championship_id: input.championshipId,
          template_id: input.templateId,
          group_key: input.groupKey,
          image_url: input.imageUrl,
        },
        { onConflict: 'championship_id,template_id,group_key' },
      )
      .select()
      .single()) as PostgrestSingleResponse<MediaManagerRenderedImageDto>;

    if (error) {
      throw classifyPostgrestError(error, 'upsert', {
        championshipId: input.championshipId,
        templateId: input.templateId,
        groupKey: input.groupKey,
      });
    }

    return mediaManagerRenderedImageDataMapper(data);
  }

  async getById(id: string): Promise<MediaManagerRenderedImage | null> {
    const { data, error } = (await this.supabase
      .from(RENDERED_IMAGES_TABLE)
      .select('*')
      .eq('id', id)
      .maybeSingle()) as PostgrestSingleResponse<MediaManagerRenderedImageDto | null>;

    if (error) {
      throw classifyPostgrestError(error, 'select', { id });
    }

    return data ? mediaManagerRenderedImageDataMapper(data) : null;
  }

  async getByMatchAndTemplate(
    matchId: number,
    templateId: number,
  ): Promise<MediaManagerRenderedImage | null> {
    const { data, error } = (await this.supabase
      .from(RENDERED_IMAGES_TABLE)
      .select('*')
      .eq('match_id', matchId)
      .eq('template_id', templateId)
      .maybeSingle()) as PostgrestSingleResponse<MediaManagerRenderedImageDto | null>;

    if (error) {
      throw classifyPostgrestError(error, 'select', { matchId, templateId });
    }

    return data ? mediaManagerRenderedImageDataMapper(data) : null;
  }

  async getByStageAndTemplate(
    stageId: number,
    templateId: number,
  ): Promise<MediaManagerRenderedImage[]> {
    const { data, error } = (await this.supabase
      .from(RENDERED_IMAGES_TABLE)
      .select('*')
      .eq('stage_id', stageId)
      .eq('template_id', templateId)) as {
      data: MediaManagerRenderedImageDto[] | null;
      error: PostgrestError | null;
    };

    if (error) {
      throw classifyPostgrestError(error, 'select', { stageId, templateId });
    }

    return (data ?? []).map(mediaManagerRenderedImageDataMapper);
  }

  async getByChampionshipAndTemplate(
    championshipId: number,
    templateId: number,
    groupKey: string,
  ): Promise<MediaManagerRenderedImage | null> {
    const { data, error } = (await this.supabase
      .from(RENDERED_IMAGES_TABLE)
      .select('*')
      .eq('championship_id', championshipId)
      .eq('template_id', templateId)
      .eq('group_key', groupKey)
      .maybeSingle()) as PostgrestSingleResponse<MediaManagerRenderedImageDto | null>;

    if (error) {
      throw classifyPostgrestError(error, 'select', { championshipId, templateId, groupKey });
    }

    return data ? mediaManagerRenderedImageDataMapper(data) : null;
  }

  async deleteById(id: string): Promise<void> {
    const { error } = await this.supabase.from(RENDERED_IMAGES_TABLE).delete().eq('id', id);

    if (error) {
      throw classifyPostgrestError(error, 'delete', { id });
    }
  }
}

const classifyPostgrestError = (
  err: PostgrestError,
  op: 'upsert' | 'select' | 'delete',
  ctx: Record<string, unknown>,
): RenderImageError => {
  const ctxStr = Object.entries(ctx)
    .map(([k, v]) => `${k}=${String(v)}`)
    .join(' ');

  if (err.code === '23505') {
    return new RenderImageError(
      RenderImageErrorCode.DUPLICATE_IMAGE,
      `[supabase] unique violation during ${op} (${ctxStr}): ${err.message}`,
      'An image for this target already exists.',
      err,
    );
  }
  if (err.code === '23503') {
    return new RenderImageError(
      RenderImageErrorCode.INVALID_PAYLOAD,
      `[supabase] FK violation during ${op} (${ctxStr}): ${err.message}`,
      'The referenced template no longer exists.',
      err,
    );
  }
  if (op === 'delete') {
    return new RenderImageError(
      RenderImageErrorCode.DELETION_FAILED,
      `[supabase] delete failed (${ctxStr}): ${err.message}`,
      'Failed to remove the image record. Please try again.',
      err,
    );
  }
  return new RenderImageError(
    RenderImageErrorCode.DB_INSERT_FAILED,
    `[supabase] ${op} failed (${ctxStr}) code=${err.code}: ${err.message}`,
    'Failed to record the image. Please try again.',
    err,
  );
};
